/**
 * Hero Slideshow — Custom Element
 * Crossfade carousel with image + video support, auto-rotation, and progress ring.
 */
class HeroSlideshow extends HTMLElement {
  constructor() {
    super();

    this.slides = Array.from(this.querySelectorAll('.hero-slide'));
    this.dots = Array.from(this.querySelectorAll('.hero-slideshow__dot'));
    this.prevBtn = this.querySelector('.hero-slideshow__arrow--prev');
    this.nextBtn = this.querySelector('.hero-slideshow__arrow--next');
    this.pauseBtn = this.querySelector('.hero-slideshow__pause');

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.autoplayTimer = null;
    this.isPlaying = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(this.dataset.speed, 10) || 6) * 1000;

    if (this.totalSlides <= 1) return;

    this.bindEvents();
    if (this.isPlaying) this.play();
    this.handleActiveSlideVideo();
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goTo(this.currentIndex + 1));
    }
    if (this.pauseBtn) {
      this.pauseBtn.addEventListener('click', () => this.toggleAutoplay());
    }

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.slideIndex, 10);
        this.goTo(idx);
      });
    });

    // Pause on hover / focus
    this.addEventListener('mouseenter', () => this.pauseIfPlaying());
    this.addEventListener('mouseleave', () => this.resumeIfPlaying());
    this.addEventListener('focusin', () => this.pauseIfPlaying());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.resumeIfPlaying();
    });

    // Listen for Shopify theme editor events
    if (Shopify && Shopify.designMode) {
      document.addEventListener('shopify:block:select', (e) => {
        if (!this.contains(e.target)) return;
        const slide = e.target.closest('.hero-slide');
        if (slide) {
          const idx = parseInt(slide.dataset.slideIndex, 10);
          this.pause();
          this.goTo(idx);
        }
      });
    }
  }

  goTo(index) {
    // Wrap around
    if (index < 0) index = this.totalSlides - 1;
    if (index >= this.totalSlides) index = 0;
    if (index === this.currentIndex) return;

    const prevSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];

    // Deactivate current
    prevSlide.classList.remove('hero-slide--active');
    prevSlide.setAttribute('aria-hidden', 'true');
    this.pauseSlideVideo(prevSlide);

    // Activate next
    nextSlide.classList.remove('hero-slide--active'); // force reflow for animation
    void nextSlide.offsetWidth;
    nextSlide.classList.add('hero-slide--active');
    nextSlide.setAttribute('aria-hidden', 'false');

    // Update dots
    if (this.dots[this.currentIndex]) {
      this.dots[this.currentIndex].classList.remove('hero-slideshow__dot--active');
    }
    if (this.dots[index]) {
      this.dots[index].classList.remove('hero-slideshow__dot--active');
      void this.dots[index].offsetWidth;
      this.dots[index].classList.add('hero-slideshow__dot--active');
    }

    this.currentIndex = index;

    // Handle video on new slide
    this.handleActiveSlideVideo();

    // Reset autoplay timer
    if (this.isPlaying) {
      this.restartTimer();
    }
  }

  handleActiveSlideVideo() {
    const activeSlide = this.slides[this.currentIndex];
    if (!activeSlide) return;

    if (activeSlide.dataset.slideType === 'video') {
      const video = activeSlide.querySelector('video');
      if (video) {
        video.currentTime = 0;
        const playPromise = video.play();
        if (playPromise) {
          playPromise.catch(() => {});
        }
        activeSlide.setAttribute('data-video-playing', 'true');

        // When video ends, go to next slide
        video.removeEventListener('ended', this._onVideoEnd);
        this._onVideoEnd = () => {
          activeSlide.removeAttribute('data-video-playing');
          this.goTo(this.currentIndex + 1);
        };
        video.addEventListener('ended', this._onVideoEnd, { once: true });
      }
    }
  }

  pauseSlideVideo(slide) {
    if (!slide) return;
    const video = slide.querySelector('video');
    if (video) {
      video.pause();
      slide.removeAttribute('data-video-playing');
    }
  }

  play() {
    this.isPlaying = true;
    this.restartTimer();
    if (this.pauseBtn) this.pauseBtn.dataset.playing = 'true';
  }

  pause() {
    this.isPlaying = false;
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
    if (this.pauseBtn) this.pauseBtn.dataset.playing = 'false';
  }

  restartTimer() {
    clearInterval(this.autoplayTimer);

    // For video slides, don't use the timer — the video end event handles it
    const activeSlide = this.slides[this.currentIndex];
    if (activeSlide && activeSlide.dataset.slideType === 'video') {
      const video = activeSlide.querySelector('video');
      if (video && !video.ended) return;
    }

    this.autoplayTimer = setInterval(() => {
      this.goTo(this.currentIndex + 1);
    }, this.autoplaySpeed);
  }

  toggleAutoplay() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  pauseIfPlaying() {
    if (this.isPlaying) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resumeIfPlaying() {
    if (this.isPlaying) {
      this.restartTimer();
    }
  }

  disconnectedCallback() {
    clearInterval(this.autoplayTimer);
  }
}

customElements.define('hero-slideshow', HeroSlideshow);
